import { useState, useRef, useEffect } from "react";
import { Lightbulb, Loader2, X } from "lucide-react";
import { useAITutor, type TutorHelpData } from "@/hooks/useAITutor";
import { LumiVoiceButton } from "@/components/lumi/LumiVoiceButton";
import { LumiAvatar } from "@/components/lumi/LumiAvatar";

interface Props {
  lessonId: string;
  lessonName: string;
  courseName: string;
  currentQuestion?: string;
  className?: string;
  onHintUsed?: () => void;
}

const FALLBACK: TutorHelpData = {
  answer: "Think about the key money idea in this question and what result each choice would create.",
  analogy: "It's like testing which tool matches the job instead of grabbing the first one you see.",
  key_takeaway: "Match the answer to the core concept, not just a familiar word.",
};

export default function QuickHintButton({ lessonId, lessonName, courseName, currentQuestion, className, onHintUsed }: Props) {
  const { loading, getTutorHint } = useAITutor();
  const [hint, setHint] = useState<TutorHelpData | null>(null);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHint(null);
    setOpen(false);
    setFetched(false);
  }, [currentQuestion]);

  const handleClick = async () => {
    if (open) {
      setOpen(false);
      return;
    }

    setOpen(true);

    if (fetched) return;
    setFetched(true);
    onHintUsed?.();

    const result = await getTutorHint({
      question: "Help me before I answer",
      lessonId,
      lessonName,
      courseName,
      currentQuestion,
      responseMode: "short",
    });

    if (result.fallback || !result.data) {
      setHint(FALLBACK);
    } else {
      setHint(result.data);
    }
  };

  return (
    <div className={`relative ${className || ""}`}>
      <button
        onClick={handleClick}
        disabled={loading && !hint}
        className="flex items-center gap-1 rounded-full border border-secondary/30 bg-secondary/10 px-2.5 py-1.5 text-[10px] font-semibold text-secondary transition-all hover:border-secondary/50 hover:bg-secondary/20 disabled:opacity-50 sm:gap-1.5 sm:px-3 sm:text-[11px]"
        title="Quick hint"
      >
        {loading && !hint ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lightbulb className="h-3 w-3" />}
        Hint
      </button>

      {open && (
        <div
          ref={panelRef}
          className="fixed right-3 z-[60] w-72 max-w-[calc(100vw-1.5rem)] max-h-[70vh] overflow-y-auto animate-fade-in rounded-xl border border-secondary/20 bg-card p-3 shadow-xl"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)',
          }}
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <LumiAvatar size="xs" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-secondary">Lumi’s Hint</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </div>

          {loading && !hint ? (
            <div className="flex items-center justify-center gap-2 py-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-secondary" />
              <span className="text-[10px] text-muted-foreground">Thinking…</span>
            </div>
          ) : hint ? (
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <p className="flex-1 text-xs leading-relaxed text-foreground">{hint.answer}</p>
                <LumiVoiceButton text={hint.answer} contextId={lessonId} label="Hear Lumi read this hint" />
              </div>
              <p className="text-[10px] italic text-muted-foreground">💡 {hint.analogy}</p>
              <div className="rounded-lg border border-secondary/10 bg-secondary/5 px-2 py-1.5">
                <p className="text-[10px] text-muted-foreground">
                  <span className="font-semibold text-secondary">Key idea:</span> {hint.key_takeaway}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
